<template>
  <div></div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, shallowRef } from 'vue'
import * as THREE from 'three'

defineOptions({
  name: 'RadarPlayer3D_TrackingRegion',
})

interface Region {
  x_cm_start: number
  x_cm_stop: number
  y_cm_start: number
  y_cm_stop: number
  z_cm_start: number
  z_cm_stop: number
}

interface Props {
  region: Region
}

const props = defineProps<Props>()

// 创建组，用于容纳所有元素 - 使用shallowRef避免Vue深度代理Three.js对象
const group = shallowRef(new THREE.Group())

// 导出group，供父组件使用
defineExpose({
  group,
})

// 计算尺寸和位置
const width = computed(() => {
  const x0 = props.region.x_cm_start / 100
  const x1 = props.region.x_cm_stop / 100
  return Math.abs(x1 - x0)
})

const depth = computed(() => {
  const y0 = props.region.y_cm_start / 100
  const y1 = props.region.y_cm_stop / 100
  return Math.abs(y1 - y0)
})

const height = computed(() => {
  const z0 = props.region.z_cm_start / 100
  const z1 = props.region.z_cm_stop / 100
  return Math.abs(z1 - z0)
})

const centerX = computed(() => {
  const x0 = props.region.x_cm_start / 100
  const x1 = props.region.x_cm_stop / 100
  return (x0 + x1) / 2
})

const centerY = computed(() => {
  const y0 = props.region.y_cm_start / 100
  const y1 = props.region.y_cm_stop / 100
  return (y0 + y1) / 2
})

const centerZ = computed(() => {
  const z0 = props.region.z_cm_start / 100
  const z1 = props.region.z_cm_stop / 100
  return (z0 + z1) / 2
})

// 创建跟踪区域
const createTrackingRegion = () => {
  // 清空现有元素
  while (group.value.children.length > 0) {
    group.value.remove(group.value.children[0])
  }

  // 设置组的位置
  group.value.position.set(centerX.value, centerY.value, centerZ.value)

  // 创建透明立方体
  const boxGeometry = new THREE.BoxGeometry(width.value, depth.value, height.value)
  const boxMaterial = new THREE.MeshStandardMaterial({
    color: 0xddddfa,
    opacity: 0.2,
    transparent: true,
    depthWrite: false,
  })
  const boxMesh = new THREE.Mesh(boxGeometry, boxMaterial)
  group.value.add(boxMesh)

  // 创建边缘线
  const edgesGeometry = new THREE.EdgesGeometry(boxGeometry)
  const edgesMaterial = new THREE.LineBasicMaterial({
    color: 0x666666,
    linewidth: 1,
  })
  const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial)
  group.value.add(edges)
}

// 监听region变化，更新跟踪区域
watch(
  () => props.region,
  () => {
    createTrackingRegion()
  },
  { deep: true, immediate: true }
)

// 挂载时创建跟踪区域
onMounted(() => {
  createTrackingRegion()
})
</script>

<style scoped lang="less"></style>
